/**
 * Client-Side Observability - Life OS
 *
 * Lightweight observability for browser/PWA environment.
 * Provides:
 * - Structured logging with levels
 * - Error tracking and reporting
 * - Performance measurement
 * - User analytics (privacy-respecting)
 *
 * In production, integrate with services like:
 * - Sentry (error tracking)
 * - PostHog/Plausible (analytics)
 * - LogRocket (session replay)
 */

// ============================================================
// CONFIGURATION
// ============================================================

interface ObservabilityConfig {
  /** Application name for logs */
  appName: string;
  /** Current environment */
  environment: 'development' | 'production' | 'test';
  /** Minimum log level to output */
  minLogLevel: LogLevel;
  /** Enable console output */
  enableConsole: boolean;
  /** Enable performance tracking */
  enablePerformance: boolean;
  /** Error reporting endpoint (optional) */
  errorReportingUrl?: string;
  /** Analytics endpoint (optional) */
  analyticsUrl?: string;
}

const defaultConfig: ObservabilityConfig = {
  appName: 'life-os',
  environment: (import.meta.env.MODE || 'development') as ObservabilityConfig['environment'],
  minLogLevel: import.meta.env.MODE === 'production' ? 'warn' : 'debug',
  enableConsole: true,
  enablePerformance: true,
};

let config: ObservabilityConfig = { ...defaultConfig };

/**
 * Initialize observability with custom config
 */
export function initObservability(customConfig: Partial<ObservabilityConfig>): void {
  config = { ...defaultConfig, ...customConfig };
  logger.info('Observability initialized', { config: { ...config, errorReportingUrl: '***' } });
}

// ============================================================
// LOGGING
// ============================================================

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LOG_LEVEL_SEVERITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  app: string;
  environment: string;
  data?: Record<string, unknown>;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
}

/**
 * Structured logger for client-side logging
 */
class Logger {
  private formatEntry(level: LogLevel, message: string, data?: Record<string, unknown>): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      app: config.appName,
      environment: config.environment,
      data,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVEL_SEVERITY[level] >= LOG_LEVEL_SEVERITY[config.minLogLevel];
  }

  private output(entry: LogEntry): void {
    if (!config.enableConsole) return;

    const consoleMethod = entry.level === 'debug' ? 'log' : entry.level;
    const prefix = `[${entry.app}]`;

    if (config.environment === 'development') {
      // Pretty output in development
      const style = {
        debug: 'color: #6b7280',
        info: 'color: #10b981',
        warn: 'color: #f59e0b',
        error: 'color: #ef4444',
      }[entry.level];

      console[consoleMethod](
        `%c${prefix} ${entry.message}`,
        style,
        entry.data || ''
      );
    } else {
      // JSON output in production (for log aggregation)
      console[consoleMethod](JSON.stringify(entry));
    }
  }

  debug(message: string, data?: Record<string, unknown>): void {
    if (!this.shouldLog('debug')) return;
    this.output(this.formatEntry('debug', message, data));
  }

  info(message: string, data?: Record<string, unknown>): void {
    if (!this.shouldLog('info')) return;
    this.output(this.formatEntry('info', message, data));
  }

  warn(message: string, data?: Record<string, unknown>): void {
    if (!this.shouldLog('warn')) return;
    this.output(this.formatEntry('warn', message, data));
  }

  error(message: string, error?: Error | unknown, data?: Record<string, unknown>): void {
    if (!this.shouldLog('error')) return;

    const entry = this.formatEntry('error', message, data);

    if (error instanceof Error) {
      entry.error = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    } else if (error) {
      entry.error = {
        name: 'Unknown',
        message: String(error),
      };
    }

    this.output(entry);

    // Report to error tracking service if configured
    if (config.errorReportingUrl && error) {
      this.reportError(entry);
    }
  }

  private async reportError(entry: LogEntry): Promise<void> {
    if (!config.errorReportingUrl) return;

    try {
      await fetch(config.errorReportingUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });
    } catch {
      // Silently fail - don't want error reporting to cause more errors
    }
  }
}

export const logger = new Logger();

// ============================================================
// ERROR TRACKING
// ============================================================

interface ErrorContext {
  componentStack?: string;
  userId?: string;
  page?: string;
  action?: string;
  [key: string]: unknown;
}

/**
 * Track and report errors
 */
export function trackError(error: Error, context?: ErrorContext): void {
  logger.error('Tracked error', error, context);

  // In production, you would send to Sentry/etc:
  // if (config.environment === 'production') {
  //   Sentry.captureException(error, { extra: context });
  // }
}

/**
 * Create error tracking wrapper for async functions
 */
export function withErrorTracking<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  context?: ErrorContext
): T {
  return (async (...args: unknown[]) => {
    try {
      return await fn(...args);
    } catch (error) {
      trackError(error instanceof Error ? error : new Error(String(error)), context);
      throw error;
    }
  }) as T;
}

// ============================================================
// PERFORMANCE TRACKING
// ============================================================

interface PerformanceMark {
  name: string;
  startTime: number;
  data?: Record<string, unknown>;
}

const activeMarks = new Map<string, PerformanceMark>();

/**
 * Start a performance measurement
 */
export function perfStart(name: string, data?: Record<string, unknown>): void {
  if (!config.enablePerformance) return;

  activeMarks.set(name, {
    name,
    startTime: performance.now(),
    data,
  });
}

/**
 * End a performance measurement and log the result
 */
export function perfEnd(name: string, additionalData?: Record<string, unknown>): number | null {
  if (!config.enablePerformance) return null;

  const mark = activeMarks.get(name);
  if (!mark) {
    logger.warn(`Performance mark "${name}" not found`);
    return null;
  }

  const duration = performance.now() - mark.startTime;
  activeMarks.delete(name);

  logger.debug(`Performance: ${name}`, {
    durationMs: Math.round(duration * 100) / 100,
    ...mark.data,
    ...additionalData,
  });

  return duration;
}

/**
 * Measure async function execution time
 */
export async function withPerformance<T>(
  name: string,
  fn: () => Promise<T>,
  data?: Record<string, unknown>
): Promise<T> {
  perfStart(name, data);
  try {
    return await fn();
  } finally {
    perfEnd(name);
  }
}

// ============================================================
// ANALYTICS
// ============================================================

interface AnalyticsEvent {
  event: string;
  properties?: Record<string, unknown>;
  timestamp: string;
  sessionId: string;
}

// Simple session ID (not persistent across page loads)
const sessionId = Math.random().toString(36).substring(2, 15);

/**
 * Track an analytics event
 */
export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  const analyticsEvent: AnalyticsEvent = {
    event,
    properties,
    timestamp: new Date().toISOString(),
    sessionId,
  };

  logger.debug('Analytics event', analyticsEvent as unknown as Record<string, unknown>);

  // In production, send to analytics service:
  // if (config.analyticsUrl) {
  //   navigator.sendBeacon(config.analyticsUrl, JSON.stringify(analyticsEvent));
  // }
}

/**
 * Track page view
 */
export function trackPageView(page: string, properties?: Record<string, unknown>): void {
  trackEvent('page_view', { page, ...properties });
}

/**
 * Track user action
 */
export function trackAction(action: string, properties?: Record<string, unknown>): void {
  trackEvent('user_action', { action, ...properties });
}

// ============================================================
// GLOBAL ERROR HANDLERS
// ============================================================

/**
 * Set up global error handlers
 * Call this once at app startup
 */
export function setupGlobalErrorHandlers(): void {
  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    trackError(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      { type: 'unhandledrejection' }
    );
  });

  // Uncaught errors
  window.addEventListener('error', (event) => {
    trackError(event.error || new Error(event.message), {
      type: 'uncaughterror',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  logger.info('Global error handlers installed');
}

// ============================================================
// WEB VITALS (Core Web Vitals tracking)
// ============================================================

interface WebVitalsMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

/**
 * Report Web Vitals metrics
 * Integrates with web-vitals library if available
 */
export function reportWebVitals(metric: WebVitalsMetric): void {
  logger.debug('Web Vital', {
    name: metric.name,
    value: Math.round(metric.value),
    rating: metric.rating,
  });

  trackEvent('web_vital', {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
  });
}

// ============================================================
// HEALTH CHECK
// ============================================================

interface HealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy';
  checks: {
    name: string;
    status: 'pass' | 'fail';
    message?: string;
  }[];
  timestamp: string;
}

/**
 * Perform client-side health check
 */
export function getHealthStatus(): HealthStatus {
  const checks: HealthStatus['checks'] = [];

  // Check localStorage availability
  try {
    localStorage.setItem('__health_check__', '1');
    localStorage.removeItem('__health_check__');
    checks.push({ name: 'localStorage', status: 'pass' });
  } catch {
    checks.push({ name: 'localStorage', status: 'fail', message: 'localStorage not available' });
  }

  // Check IndexedDB availability
  if (typeof indexedDB !== 'undefined') {
    checks.push({ name: 'indexedDB', status: 'pass' });
  } else {
    checks.push({ name: 'indexedDB', status: 'fail', message: 'IndexedDB not available' });
  }

  // Check online status
  checks.push({
    name: 'network',
    status: navigator.onLine ? 'pass' : 'fail',
    message: navigator.onLine ? undefined : 'Offline',
  });

  // Determine overall status
  const failedChecks = checks.filter((c) => c.status === 'fail');
  const status: HealthStatus['status'] =
    failedChecks.length === 0
      ? 'healthy'
      : failedChecks.length < checks.length / 2
        ? 'degraded'
        : 'unhealthy';

  return {
    status,
    checks,
    timestamp: new Date().toISOString(),
  };
}

// ============================================================
// EXPORTS
// ============================================================

export type { LogLevel, LogEntry, ErrorContext, PerformanceMark, AnalyticsEvent, HealthStatus };
